import rollbar from '@mortgage-pos/ui/services/rollbar'
import { ExperimentDefinition } from '../experiments/experimentRegistry'

// Add global typings to make TypeScript happy with window.cohesion and window.monarch
declare global {
  interface Window {
    cohesionScriptLoadFailed: boolean
    cohesion: (event: string, callback: Function) => void
    monarch: (
      type: string,
      ruleId: string,
      contexts: Record<string, unknown>,
      options: Record<string, unknown>,
      callback: (err: Error | null, result: Record<string, any> | null) => void
    ) => void
  }

  interface MonarchEvaluation {
    source: Record<
      string,
      {
        returnType: string
        isRuleActive: boolean
        audienceId: string // UUID
        audienceName: string
      }
    > & {
      trafficFlow: {
        value: any // Could be a string, object, etc.
        returnType: string
        returnDetails: {
          type: string
          returnValueId: string // UUID
          returnValueName: string
        }
        isRuleActive: boolean
        isDraft: boolean
        audienceId: string // UUID
        audienceName: string
      }
    }
  }
}

// Template mapping configuration for welcome page variants
const TEMPLATE_MAP = {
  'Form Landing Page | Welcome Screen v1 | Pop Experience': 'pop',
  'Form Landing Page | Welcome Screen v1 | Simple Experience': 'simple',
}

/**
 * Evaluates the welcome page variant using the monarch service
 * @returns The template identifier: 'pop', 'simple', or 'default'
 */
export async function evaluateWelcomePageVariant(): Promise<string> {
  if (window.cohesionScriptLoadFailed) {
    return 'default'
  }

  try {
    const monarchPromise = new Promise<string>((resolve) => {
      window.cohesion('monarch:ready', function () {
        window.monarch(
          'rule',
          'sageFormLandingPageWelcomeScreenV1',
          {},
          {},
          function (err, result) {
            if (err) {
              resolve('default')
            } else {
              const monarchResult = result.sageFormLandingPageWelcomeScreenV1
              resolve(
                monarchResult
                  ? TEMPLATE_MAP[monarchResult] || 'default'
                  : 'default'
              )
            }
          }
        )
      })
    })

    const timeoutPromise = new Promise<string>((resolve) => {
      setTimeout(() => resolve('default'), 2000)
    })

    return await Promise.race([monarchPromise, timeoutPromise])
  } catch (error) {
    console.error('Error evaluating welcome page variant:', error)
    return 'default'
  }
}

// Template mapping configuration for welcome page variants
const TEMPLATE_MAP_V2 = {
  'Form Landing Page | Welcome Screen v2 | Pop Experience': 'popV2',
  'Form Landing Page | Welcome Screen v2 | Simple Experience': 'simpleV2',
}

/**
 * Evaluates the welcome page variant using the monarch service
 * @returns The template identifier: 'pop', 'simple', or 'default'
 */
export async function evaluateWelcomePageVariantV2(): Promise<string> {
  if (window.cohesionScriptLoadFailed) {
    return 'default'
  }

  try {
    const monarchPromise = new Promise<string>((resolve) => {
      window.cohesion('monarch:ready', function () {
        window.monarch(
          'rule',
          'sageFormLandingPageWelcomeScreenV2',
          {},
          {},
          function (err, result) {
            if (err) {
              resolve('default')
            } else {
              const monarchResult = result.sageFormLandingPageWelcomeScreenV2
              resolve(
                monarchResult
                  ? TEMPLATE_MAP_V2[monarchResult] || 'default'
                  : 'default'
              )
            }
          }
        )
      })
    })

    const timeoutPromise = new Promise<string>((resolve) => {
      setTimeout(() => resolve('default'), 2000)
    })

    return await Promise.race([monarchPromise, timeoutPromise])
  } catch (error) {
    console.error('Error evaluating welcome page variant:', error)
    return 'default'
  }
}

interface ConsentExperimentContext extends Record<string, unknown> {
  applicationGuid?: string
  questionSetSlug?: string
}

/**
 * Evaluates the consent UX experiment using the monarch service
 * @param context - Context data passed to Monarch for targeting
 * @returns The experiment variant: 'treatment' or 'control'
 */
export async function evaluateConsentExperiment(
  context: ConsentExperimentContext = {}
): Promise<'treatment' | 'control'> {
  if (window.cohesionScriptLoadFailed) {
    return 'control'
  }

  try {
    const monarchPromise = new Promise<'treatment' | 'control'>((resolve) => {
      const callMonarchRule = () => {
        window.monarch(
          'rule',
          'consentUxExperiment',
          context,
          {},
          function (err, result) {
            if (err) {
              resolve('control')
            } else {
              const variant = result?.consentUxExperiment
              resolve(variant === 'treatment' ? 'treatment' : 'control')
            }
          }
        )
      }

      // If monarch is already available, call it directly
      if (typeof window.monarch === 'function') {
        callMonarchRule()
      } else {
        // Otherwise wait for monarch:ready
        window.cohesion('monarch:ready', function () {
          callMonarchRule()
        })
      }
    })

    const timeoutPromise = new Promise<'treatment' | 'control'>((resolve) => {
      setTimeout(() => resolve('control'), 2000)
    })

    return await Promise.race([monarchPromise, timeoutPromise])
  } catch (error) {
    console.error('Error evaluating consent experiment:', error)
    return 'control'
  }
}

// "monarch:done" is fired when the monarch evaluation is complete
export async function getMonarchEvaluationResult(): Promise<MonarchEvaluation> {
  if (window.cohesionScriptLoadFailed) {
    throw new Error('Cohesion script failed to load')
  }

  const monarchPromise = new Promise<MonarchEvaluation>((resolve, reject) => {
    window.cohesion(
      'monarch:done',
      function (err: Error | null, evaluation: MonarchEvaluation) {
        if (err) {
          return reject(err)
        }

        resolve(evaluation)
      }
    )
  })

  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('Monarch evaluation timed out')), 2000)
  })

  return Promise.race([monarchPromise, timeoutPromise])
}

const EXPERIMENT_TIMEOUT_MS = 2000

function hasRequiredContext(
  definition: ExperimentDefinition<string>,
  context: Record<string, unknown>
): boolean {
  return (definition.requiredContextKeys ?? []).every((key) => {
    const value = context[key]
    return value !== undefined && value !== null && value !== ''
  })
}

function toKnownVariant<TVariant extends string>(
  definition: ExperimentDefinition<TVariant>,
  value: unknown
): TVariant {
  if (
    typeof value === 'string' &&
    definition.variants.includes(value as TVariant)
  ) {
    return value as TVariant
  }

  // A value we don't recognise means the admin config drifted from the code.
  if (value !== undefined && value !== null) {
    rollbar.error('Monarch experiment returned an unrecognized variant', {
      ruleCustomId: definition.ruleCustomId,
      value,
    })
  }

  return definition.defaultVariant
}

function requestExperimentVariant<TVariant extends string>(
  definition: ExperimentDefinition<TVariant>,
  context: Record<string, unknown>
): Promise<TVariant> {
  const monarchPromise = new Promise<TVariant>((resolve) => {
    const requestVariant = () => {
      window.monarch(
        'rule',
        definition.ruleCustomId,
        context,
        {},
        (err, result) => {
          if (err) {
            console.error(
              `Monarch experiment ${definition.ruleCustomId} failed`,
              err
            )
            return resolve(definition.defaultVariant)
          }

          resolve(toKnownVariant(definition, result?.[definition.ruleCustomId]))
        }
      )
    }

    // Mid-flow screens mount long after Cohesion is ready, so check the global
    // first — waiting on monarch:ready alone would miss an event already fired.
    if (typeof window.monarch === 'function') {
      requestVariant()
      return
    }

    window.cohesion('monarch:ready', requestVariant)
  })

  const timeoutPromise = new Promise<TVariant>((resolve) => {
    setTimeout(() => resolve(definition.defaultVariant), EXPERIMENT_TIMEOUT_MS)
  })

  return Promise.race([monarchPromise, timeoutPromise])
}

export function evaluateExperimentVariant<TVariant extends string>(
  definition: ExperimentDefinition<TVariant>,
  context: Record<string, unknown> = {}
): Promise<TVariant> {
  if (window.cohesionScriptLoadFailed) {
    return Promise.resolve(definition.defaultVariant)
  }

  if (!hasRequiredContext(definition, context)) {
    return Promise.resolve(definition.defaultVariant)
  }

  // A synchronous throw in the executor (window.cohesion undefined under CSP)
  // rejects rather than resolves; all three existing helpers guard this.
  return requestExperimentVariant(definition, context).catch((error) => {
    console.error(`Monarch experiment ${definition.ruleCustomId} failed`, error)
    return definition.defaultVariant
  })
}
